import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Image from '../../../components/AppImage';

const ShopList = ({ 
  shops = [], 
  selectedShop = null, 
  onShopSelect = () => {},
  onGetDirections = () => {},
  onCallShop = () => {},
  onViewDetails = () => {},
  userLocation = null,
  filters = {}
}) => {
  const [sortBy, setSortBy] = useState('distance');

  const calculateDistance = (shopCoords) => {
    if (!userLocation) return 0;
    
    const R = 3959; // Earth's radius in miles
    const dLat = (shopCoords?.lat - userLocation?.lat) * Math.PI / 180;
    const dLng = (shopCoords?.lng - userLocation?.lng) * Math.PI / 180;
    const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
              Math.cos(userLocation?.lat * Math.PI / 180) * Math.cos(shopCoords?.lat * Math.PI / 180) *
              Math.sin(dLng/2) * Math.sin(dLng/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c;
  };

  const getShopHours = (shop) => {
    const today = new Date()?.getDay();
    const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const todayHours = shop?.hours?.[days?.[today]];
    
    if (!todayHours || todayHours === 'closed') {
      return { status: 'closed', text: 'Closed today' };
    }
    
    const now = new Date();
    const currentTime = now?.getHours() * 60 + now?.getMinutes();
    const [openTime, closeTime] = todayHours?.split('-')?.map(time => {
      const [hours, minutes] = time?.trim()?.split(':')?.map(Number);
      return hours * 60 + (minutes || 0);
    });
    
    if (currentTime >= openTime && currentTime <= closeTime) {
      return { status: 'open', text: `Open until ${todayHours?.split('-')?.[1]?.trim()}` };
    } else if (currentTime < openTime) {
      return { status: 'closed', text: `Opens at ${todayHours?.split('-')?.[0]?.trim()}` };
    } else {
      return { status: 'closed', text: 'Closed' };
    }
  };

  const filteredAndSortedShops = shops?.filter(shop => {
      if (filters?.services && filters?.services?.length > 0) {
        return filters?.services?.some(service => shop?.services?.includes(service));
      }
      if (filters?.brands && filters?.brands?.length > 0) {
        return filters?.brands?.some(brand => shop?.brands?.includes(brand));
      }
      if (filters?.minRating) {
        return shop?.rating >= filters?.minRating;
      }
      return true;
    })?.sort((a, b) => {
      switch (sortBy) {
        case 'distance':
          return calculateDistance(a?.coordinates) - calculateDistance(b?.coordinates);
        case 'rating':
          return b?.rating - a?.rating;
        case 'name':
          return a?.name?.localeCompare(b?.name);
        default:
          return 0;
      }
    });

  const ShopCard = ({ shop }) => {
    const distance = calculateDistance(shop?.coordinates);
    const hours = getShopHours(shop);
    const isSelected = selectedShop?.id === shop?.id;

    return (
      <div 
        className={`bg-card border rounded-lg p-4 cursor-pointer transition-all duration-200 interactive-scale ${
          isSelected 
            ? 'border-accent shadow-md ring-2 ring-accent/20' 
            : 'border-border hover:border-accent/50 hover:shadow-sm'
        }`}
        onClick={() => onShopSelect(shop)}
      >
        {/* Header */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1">
            <div className="flex items-center space-x-2 mb-1">
              <h3 className="font-semibold text-foreground text-sm">{shop?.name}</h3>
              {shop?.isPartner && (
                <span className="bg-success text-success-foreground text-xs px-2 py-1 rounded-full font-medium">
                  Partner
                </span>
              )}
            </div>
            <p className="text-xs text-muted-foreground mb-1">{shop?.address}</p>
            <div className="flex items-center space-x-3 text-xs">
              <div className="flex items-center space-x-1">
                <Icon name="MapPin" size={12} color="var(--color-muted-foreground)" />
                <span className="text-muted-foreground">
                  {distance?.toFixed(1)} mi away
                </span>
              </div>
              <div className="flex items-center space-x-1">
                <Icon name="Star" size={12} color="var(--color-warning)" />
                <span className="text-muted-foreground">
                  {shop?.rating} ({shop?.reviewCount})
                </span>
              </div>
            </div>
          </div>
          
          {shop?.image && (
            <div className="w-16 h-16 ml-3 overflow-hidden rounded-md">
              <Image
                src={shop?.image}
                alt={shop?.name}
                className="w-full h-full object-cover"
              />
            </div>
          )}
        </div>
        {/* Services */}
        <div className="mb-3">
          <div className="flex flex-wrap gap-1">
            {shop?.services?.slice(0, 3)?.map((service) => (
              <span
                key={service}
                className="bg-muted text-muted-foreground text-xs px-2 py-1 rounded-md"
              >
                {service}
              </span>
            ))}
            {shop?.services?.length > 3 && (
              <span className="text-xs text-muted-foreground">
                +{shop?.services?.length - 3} more
              </span>
            )}
          </div>
        </div>
        {/* Hours */}
        <div className="flex items-center space-x-2 mb-3">
          <Icon 
            name="Clock" 
            size={12} 
            color={hours?.status === 'open' ? 'var(--color-success)' : 'var(--color-muted-foreground)'} 
          />
          <span className={`text-xs ${
            hours?.status === 'open' ? 'text-success' : 'text-muted-foreground'
          }`}>
            {hours?.text}
          </span>
        </div>
        {/* Actions */}
        <div className="flex space-x-2">
          <Button
            variant="outline"
            size="sm"
            iconName="Navigation"
            iconPosition="left"
            onClick={(e) => {
              e?.stopPropagation();
              onGetDirections(shop);
            }}
            className="flex-1"
          >
            Directions
          </Button>
          <Button
            variant="outline"
            size="sm"
            iconName="Phone"
            iconPosition="left"
            onClick={(e) => {
              e?.stopPropagation();
              onCallShop(shop);
            }}
            className="flex-1"
          >
            Call
          </Button>
          <Button
            variant="ghost"
            size="sm"
            iconName="ExternalLink"
            onClick={(e) => {
              e?.stopPropagation();
              onViewDetails(shop);
            }}
          />
        </div>
        {/* Specialties */}
        {shop?.specialties && shop?.specialties?.length > 0 && (
          <div className="mt-3 pt-3 border-t border-border">
            <div className="flex items-center space-x-2">
              <Icon name="Award" size={12} color="var(--color-accent)" />
              <span className="text-xs text-muted-foreground">
                Specializes in: {shop?.specialties?.slice(0, 2)?.join(', ')}
              </span>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-border">
        <div>
          <h2 className="text-lg font-semibold text-foreground">Nearby Bike Shops</h2>
          <p className="text-sm text-muted-foreground">
            {filteredAndSortedShops?.length} shops found
          </p>
        </div>
        
        {/* Sort Options */}
        <select
          value={sortBy}
          onChange={(e) => setSortBy(e?.target?.value)}
          className="px-3 py-2 border border-input rounded-md text-sm bg-background focus:ring-2 focus:ring-ring focus:border-transparent"
        >
          <option value="distance">Sort by Distance</option>
          <option value="rating">Sort by Rating</option>
          <option value="name">Sort by Name</option>
        </select>
      </div>
      {/* Shop List */}
      <div className="flex-1 overflow-y-auto p-4">
        {filteredAndSortedShops?.length === 0 ? (
          <div className="text-center py-12">
            <Icon name="Store" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-4" />
            <h3 className="text-lg font-medium text-foreground mb-2">No shops found</h3>
            <p className="text-muted-foreground mb-4">
              Try adjusting your filters or expanding your search radius.
            </p>
            <Button variant="outline" onClick={() => window.location?.reload()}>
              Reset Filters
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredAndSortedShops?.map((shop) => (
              <ShopCard key={shop?.id} shop={shop} />
            ))}
          </div>
        )}
      </div>
      {/* Quick Actions */}
      <div className="p-4 border-t border-border bg-muted/30">
        <div className="flex space-x-2">
          <Button
            variant="outline"
            size="sm"
            iconName="Filter"
            iconPosition="left"
            className="flex-1"
          >
            More Filters
          </Button>
          <Button
            variant="outline"
            size="sm"
            iconName="RefreshCw"
            iconPosition="left"
            onClick={() => window.location?.reload()}
            className="flex-1"
          >
            Refresh
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ShopList;